import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { YmEconomicsShowcase, YmEconomicsSync, YmPriceAttractiveness, YmPriceAttractivenessLevel } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount, formatRublePerGram } from '@/lib/market-money'
import { unavailableReasonText } from '@/lib/warehouse/labels'

type Economics = NonNullable<InventoryListItem['ymarketEconomics']>

export interface YmarketShowcaseFigure {
  /** The Market showcase price without Yandex Pay, the one its cost calculator sells at. */
  amount: number | null
  unavailableReason: string
  perGram: number | null
  perGramUnavailableReason: string
  description: string
}

const MOSCOW = 'Europe/Moscow'

function moscowDay(value: Date): string {
  return value.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: MOSCOW })
}

/** «обновлено 05:02» today, «обновлено 28.09 21:11» on an earlier day, Moscow time. */
export function showcaseUpdatedText(collectedAt: string, now: Date = new Date()): string {
  const moment = new Date(collectedAt)
  const time = moment.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: MOSCOW })
  if (moscowDay(moment) === moscowDay(now)) return `обновлено ${time}`
  const day = moment.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: MOSCOW })
  return `обновлено ${day} ${time}`
}

function shortDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: MOSCOW })
}

/** Where the shown showcase comes from: the «Цены» report value and the formulas in force. */
export function formulaText(showcase: YmEconomicsShowcase | null | undefined): string | null {
  if (showcase === null || showcase === undefined || showcase.reportPrice === null) return null
  const reportPay = showcase.reportPayPrice === null ? '' : ` (с Пэй ${formatRubleAmount(showcase.reportPayPrice)})`
  const formula = showcase.formulaSetAt === null ? 'формула витрины по умолчанию' : `формула витрины от ${shortDate(showcase.formulaSetAt)}`
  return `отчёт «Цены» ${formatRubleAmount(showcase.reportPrice)}${reportPay}, ${formula}`
}

function payText(pay: number): string {
  return `с Яндекс Пэй ${formatRubleAmount(pay)}`
}

export const ATTRACTIVENESS_LABELS: Readonly<Record<YmPriceAttractivenessLevel, string>> = {
  optimal: 'выгодная',
  average: 'умеренная',
  low: 'завышенная',
}

/** «цена на Маркете умеренная: выгодно до 23 490 ₽, завышена выше 54 515 ₽» - our price against the report bounds. */
export function attractivenessText(attractiveness: YmPriceAttractiveness | null | undefined): string | null {
  if (attractiveness === null || attractiveness === undefined) return null
  return `цена на Маркете ${ATTRACTIVENESS_LABELS[attractiveness.level]}: выгодно до ${formatRubleAmount(attractiveness.greenThreshold)}, завышена выше ${formatRubleAmount(attractiveness.redThreshold)}`
}

function orderDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: MOSCOW })
}

/** «заказ 26.09: покупатель заплатил 15 816 ₽, витрина тогда 16 767 ₽ (−5,67%)». */
export function syncText(check: YmEconomicsSync | null | undefined): string | null {
  if (check === null || check === undefined) return null
  const head = `заказ ${orderDate(check.orderedAt)}: покупатель заплатил ${formatRubleAmount(check.buyerPrice)}`
  if (check.showcasePrice === null || check.deviationPercent === null) {
    return `${head}, витрину на тот момент сверить не с чем — ${unavailableReasonText(check.unavailableReason ?? 'showcase_not_collected')}`
  }
  const deviation = `${check.deviationPercent > 0 ? '+' : check.deviationPercent < 0 ? '−' : ''}${Math.abs(check.deviationPercent).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`
  return `${head}, витрина тогда ${formatRubleAmount(check.showcasePrice)} (${deviation})${check.drifted ? ' — расходится больше чем на 2%' : ''}`
}

function detailParts(economics: Economics, now: Date): string[] {
  const showcase = economics.showcase
  const pay = showcase?.payPrice ?? null
  const noPay = showcase?.noPayPrice ?? null
  const struck = economics.prices?.ymDiscountBase ?? null
  const parts: (string | null)[] = [
    pay === null || pay === noPay ? null : payText(pay),
    formulaText(showcase),
    economics.sellsFor === null ? null : `наша цена ${formatRubleAmount(economics.sellsFor)}`,
    attractivenessText(economics.attractiveness),
    syncText(economics.syncCheck),
    struck === null ? null : `зачёркнутая ${formatRubleAmount(struck)}`,
    economics.net === null ? null : `на руки ${formatRubleAmount(economics.net)}${economics.netPerGram === null ? '' : ` (${formatRublePerGram(economics.netPerGram)})`}`,
    showcase?.collectedAt ? showcaseUpdatedText(showcase.collectedAt, now) : null,
  ]
  return parts.filter((part) => part !== null)
}

/** What the Market showcase shows for a piece: one price, its ₽/g over the 1C weight, and the details for the tooltip. */
export function ymarketShowcaseFigure(
  economics: InventoryListItem['ymarketEconomics'],
  weightGrams: number | null,
  weightUnavailableReason: string | null,
  now: Date = new Date(),
): YmarketShowcaseFigure {
  if (economics === null || economics === undefined) {
    return {
      amount: null,
      unavailableReason: 'ymarket_economics_unavailable',
      perGram: null,
      perGramUnavailableReason: 'ymarket_economics_unavailable',
      description: `Цена на витрине: ${unavailableReasonText('ymarket_economics_unavailable')}`,
    }
  }
  const showcase = economics.showcase
  const amount = showcase?.noPayPrice ?? null
  const unavailableReason = showcase?.unavailableReason ?? 'showcase_not_collected'
  const details = detailParts(economics, now)
  const head = amount === null
    ? `Цена на витрине: ${unavailableReasonText(unavailableReason)}`
    : `На витрине Маркета ${formatRubleAmount(amount)} без Яндекс Пэй`
  return {
    amount,
    unavailableReason,
    perGram: amount !== null && weightGrams !== null && weightGrams > 0 ? amount / weightGrams : null,
    perGramUnavailableReason: weightUnavailableReason ?? 'unit.weight_not_recorded',
    description: [head, ...details].join(' · '),
  }
}
